import { useParams } from 'react-router-dom';

import { useWorkspaceFeaturesApi } from '@/api/workspaceFeature';
import { TIER_FEATURES, type PlanTier } from '@ditto/shared';
import { useWorkspacesService } from './workspaces';

export const useWorkspaceFeaturesService = () => {
  const { workspaceUname } = useParams<{ workspaceUname: string }>();

  // Get workspace ID from workspace uname
  const { workspaces, isPending: isWorkspacesPending } = useWorkspacesService();
  const currentWorkspace = workspaces?.find(
    (w: any) => w.uname === workspaceUname
  );
  const workspaceId = currentWorkspace?.id;
  const tier: PlanTier = currentWorkspace?.tier ?? 'free';
  const tierFeatures = TIER_FEATURES[tier];

  const {
    data: features,
    isPending: isWorkspaceFeaturesPending,
    error,
  } = useWorkspaceFeaturesApi(workspaceId || '');

  const isActivated = (featureName: string) =>
    features?.some(
      f => f.feature === featureName && f.status === 'ACTIVATED'
    ) ?? false;

  const isInTier = (featureName: string) =>
    tierFeatures.includes(featureName as any);

  return {
    features,
    isPending: isWorkspacesPending || isWorkspaceFeaturesPending,
    error,
    workspaceId,
    tier,

    // Enabled = activated AND in tier
    isChatEnabled: isActivated('chat') && isInTier('chat'),
    isLiteratureReviewEnabled:
      isActivated('literatureReview') && isInTier('literatureReview'),
    isTableViewEnabled: isActivated('tableView') && isInTier('tableView'),
    isRegulatoryWatchEnabled:
      isActivated('regulatoryWatch') && isInTier('regulatoryWatch'),
    isMcpEnabled: isActivated('mcp') && isInTier('mcp'),

    // Locked = activated but NOT in tier (shown with lock icon)
    isChatLocked: isActivated('chat') && !isInTier('chat'),
    isLiteratureReviewLocked:
      isActivated('literatureReview') && !isInTier('literatureReview'),
    isTableViewLocked: isActivated('tableView') && !isInTier('tableView'),
    isRegulatoryWatchLocked:
      isActivated('regulatoryWatch') && !isInTier('regulatoryWatch'),
  };
};
